import { z } from "zod";

export const dreamNodeSchema = z.object({
  id: z.uuid(),
  personId: z.uuid().nullable(),
  role: z.string().trim().min(1, "Role is required."),
  department: z.string().trim(),
  notes: z.string().trim(),
  reportsTo: z.uuid().nullable(),
  x: z.number().finite(),
  y: z.number().finite(),
});
export type DreamNodeInput = z.infer<typeof dreamNodeSchema>;
export interface DreamNode extends DreamNodeInput {
  name: string | null;
  email: string | null;
}
export const dreamMapInput = z
  .object({
    revision: z.number().int().positive(),
    notes: z.string().trim(),
    nodes: z.array(dreamNodeSchema),
  })
  .superRefine(({ nodes }, ctx) => {
    const ids = new Set(nodes.map((node) => node.id));
    const people = nodes.flatMap((node) => (node.personId ? [node.personId] : []));
    const fail = (message: string) => ctx.addIssue({ code: "custom", message });
    if (ids.size !== nodes.length) fail("Each chart position must have a unique ID.");
    if (new Set(people).size !== people.length)
      fail("A person can appear only once in this company map.");
    const managers = new Map(nodes.map((node) => [node.id, node.reportsTo]));
    for (const node of nodes) {
      if (node.reportsTo && !ids.has(node.reportsTo)) {
        fail("Choose a manager in this company map.");
        break;
      }
      const visited = new Set<string>();
      let id: string | null | undefined = node.id;
      while (id) {
        if (visited.has(id)) {
          fail("Reporting lines cannot form a loop.");
          return;
        }
        visited.add(id);
        id = managers.get(id);
      }
    }
  });
export type DreamMapInput = z.infer<typeof dreamMapInput>;
export const dreamCompanyInput = z.object({ companyId: z.uuid() });
export interface DreamCompany {
  id: string;
  companyId: string;
  name: string;
  domain: string | null;
  notes: string;
  revision: number;
  peopleCount: number;
  placeholderCount: number;
}
export interface DreamMap extends DreamCompany {
  nodes: DreamNode[];
}
export interface DreamPerson {
  id: string;
  name: string;
  email: string | null;
}
type Json<T> = { content: { "application/json": T } };
type Id = { path: { id: string } };
export interface DreamListPaths {
  "/dream-list": {
    get: { responses: { 200: Json<DreamCompany[]> } };
    post: {
      requestBody: Json<z.infer<typeof dreamCompanyInput>>;
      responses: { 201: Json<{ id: string }> };
    };
  };
  "/dream-list/{id}": {
    get: { parameters: Id; responses: { 200: Json<DreamMap> } };
    put: { parameters: Id; requestBody: Json<DreamMapInput>; responses: { 200: Json<DreamMap> } };
    delete: {
      parameters: Id;
      requestBody: Json<{ revision: number }>;
      responses: { 204: { content?: never } };
    };
  };
  "/dream-list/{id}/people": {
    get: { parameters: Id; responses: { 200: Json<DreamPerson[]> } };
  };
}
